import React, { useContext, useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/react";
import { useQuery } from "@apollo/client";
import { GET_CUSTOMER_USERS } from "src/api/customer";
import { GetCustomerUsers } from "src/api/generated/GetCustomerUsers";
import CustomerUserList from "src/screens/customerusers/list/CustomerUserList";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import * as Screens from "src/screens";
import { withRouter, RouteComponentProps } from "react-router";
import { selectRoute, Route } from "src/config/routes";
import classes from "./CustomerUsers.module.css";
import { getLang } from "src/utils/i18n";
import { ENUM_TYPE } from "src/stores/EnumerationStore";
import { MFA_EXCLUDED_ROLE_IDS, Role, RolesMap } from "src/network/User";
import { CustomerUsersMfaBulk, MfaBulkRoleOption } from "src/screens/customerusers/mfaBulk/CustomerUsersMfaBulk";

const CustomerUsersBase = (props: RouteComponentProps) => {
    const { t } = useTranslation();
    const { navStore, authStore, uiSidebarStore, enumerationsStore } = useContext(RootStoreContext);

    const [selectedMfaRole, setSelectedMfaRole] = useState<string>("");

    const variables = {
        planguage: getLang()
    };

    const { data, loading, error, refetch } = useQuery<GetCustomerUsers>(GET_CUSTOMER_USERS, {
        variables: variables
    });

    // The portal roles that occur among this customer's users, as the role names the MFA bulk endpoints filter by.
    const mfaBulkRoleOptions = useMemo<MfaBulkRoleOption[]>(() => {
        const roleIds = new Set<number>();
        data?.customer_users.forEach((user) => user.userroles.forEach(({ role }) => roleIds.add(role)));

        return RolesMap.filter(
            (entry) =>
                entry.role !== Role.NONE && !MFA_EXCLUDED_ROLE_IDS.includes(entry.roleid) && roleIds.has(entry.roleid)
        ).map((entry) => ({
            key: entry.key,
            roleid: entry.roleid,
            label: enumerationsStore.getEnumMapByType(ENUM_TYPE.USER_ROLES).get(entry.roleid)?.label ?? entry.label
        }));
    }, [data, enumerationsStore]);

    // The role picked in the MFA bulk card also filters the user list below it.
    const filteredUsers = useMemo(() => {
        const roleId = mfaBulkRoleOptions.find((role) => role.key === selectedMfaRole)?.roleid;

        return roleId === undefined
            ? data?.customer_users
            : data?.customer_users.filter((user) => user.userroles.some(({ role }) => role === roleId));
    }, [data, mfaBulkRoleOptions, selectedMfaRole]);

    useEffect(() => {
        navStore.setTitle(t("screens.customer_users.title"));
        navStore.setActionButtonMiddle(null);
        navStore.setActionButtonRight(null);

        if (uiSidebarStore.isSidebarActive) {
            navStore.setBackbutton(null);
        } else {
            navStore.setBackbutton(selectRoute(Route.profile), t("screens.account.title").toString());
        }
    }, [navStore, t, authStore.user?.role, uiSidebarStore.isSidebarActive]);

    if (loading) {
        return <Screens.Loading />;
    }
    if (error) {
        return <Screens.Error message={error.message} networkError={error.networkError} />;
    }
    if (!data) {
        return <Screens.Error message={t("error.nodata").toString()} />;
    }

    return (
        <div className={classes.customerUsers}>
            <UI.Row>
                <UI.Col md={12}>
                    <CustomerUsersMfaBulk
                        roleOptions={mfaBulkRoleOptions}
                        users={data.customer_users}
                        onChanged={refetch}
                        onRoleChange={setSelectedMfaRole}
                    />
                </UI.Col>
            </UI.Row>
            <UI.Row>
                <UI.Col md={12}>
                    <UI.Card>
                        <div className={classes.customerList}>
                            <CustomerUserList
                                users={filteredUsers ?? data.customer_users}
                                userrolesDefinition={data.userrolesdefinition}
                                displayUserStatusRows={true}
                            />
                        </div>
                    </UI.Card>
                </UI.Col>
            </UI.Row>
        </div>
    );
};

export const CustomerUsers = withRouter(Sentry.withProfiler(observer(CustomerUsersBase)));
